// Budget Management Service
import { supabase } from './supabase';
import { noderr, DatabaseError } from '../../shared/utils/errorHandler';
import { metaAdsService } from './metaAds';
import type { 
  BudgetCategory, 
  CampaignBudget, 
  BudgetSnapshot,
  BudgetSummary,
  CategorySummary,
  BudgetSuggestion,
  ReallocationSuggestion
} from '../../shared/types/budget';

export class BudgetService {
  // Budget Categories
  async getBudgetCategories(): Promise<BudgetCategory[]> {
    const [error, result] = await noderr(
      supabase
        .from('budget_categories')
        .select('*')
        .eq('is_active', true)
        .order('name')
    );
    
    if (error) throw new DatabaseError(`Failed to fetch budget categories: ${error.message}`);
  return ((result as any)?.data as BudgetCategory[]) || [];
  }

  async getGlobalBudgetSummary(): Promise<{ summary: BudgetSummary; active_campaigns: number }> {
    const [error, result] = await noderr(
      supabase
        .from('campaign_budgets')
        .select(`
          *,
          category:budget_categories(*)
        `)
    );

    if (error) throw new DatabaseError(`Failed to build global budget summary: ${error.message}`);

    const budgets = ((result as any)?.data as CampaignBudget[]) || [];

    const totalAllocated = budgets.reduce((sum, b) => sum + (b.allocated_amount || 0), 0);
    const totalSpent = budgets.reduce((sum, b) => sum + (b.spent_amount || 0), 0);
    const activeCampaigns = new Set(budgets.map(b => b.campaign_id)).size;

    const categoryMap = new Map<string, {
      category_id: string;
      category_name: string;
      category_color: string;
      allocated: number;
      spent: number;
    }>();

    budgets.forEach(budget => {
      const key = budget.category_id || 'uncategorized';
      const existing = categoryMap.get(key) || {
        category_id: budget.category_id || 'uncategorized',
        category_name: budget.category?.name || 'Uncategorized',
        category_color: budget.category?.color || '#3B82F6',
        allocated: 0,
        spent: 0
      };

      existing.allocated += budget.allocated_amount || 0;
      existing.spent += budget.spent_amount || 0;

      categoryMap.set(key, existing);
    });

    const categories = Array.from(categoryMap.values()).map(category => {
      const remaining = category.allocated - category.spent;
      const percentageUsed = category.allocated > 0 ? (category.spent / category.allocated) * 100 : 0;

      let status: CategorySummary['status'] = 'on_track';
      if (percentageUsed > 100) status = 'over_budget';
      else if (percentageUsed < 50) status = 'under_budget';

      const performanceScore = Math.max(0, Math.min(100, 100 - Math.abs(percentageUsed - 100)));

      return {
        category_id: category.category_id,
        category_name: category.category_name,
        category_color: category.category_color,
        allocated: category.allocated,
        spent: category.spent,
        remaining,
        percentage_used: percentageUsed,
        performance_score: performanceScore,
        status
      } as CategorySummary;
    });

    const summary: BudgetSummary = {
      campaign_id: 'global',
      total_allocated: totalAllocated,
      total_spent: totalSpent,
      remaining_budget: totalAllocated - totalSpent,
      spend_percentage: totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0,
      categories,
      performance_score: categories.length > 0
        ? categories.reduce((sum, category) => sum + category.performance_score, 0) / categories.length
        : 0,
      alerts_count: 0
    };

    return {
      summary,
      active_campaigns: activeCampaigns
    };
  }

  async createBudgetCategory(category: Omit<BudgetCategory, 'id' | 'created_at' | 'updated_at'>): Promise<BudgetCategory> {
    const [error, result] = await noderr(
      supabase
        .from('budget_categories')
        .insert(category)
        .select()
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to create budget category: ${error.message}`);
  return (result as any)?.data as BudgetCategory;
  }

  // Campaign Budgets
  async getCampaignBudgets(campaignId: string): Promise<CampaignBudget[]> {
    const [error, result] = await noderr(
      supabase
        .from('campaign_budgets')
        .select(`
          *,
          category:budget_categories(*)
        `)
        .eq('campaign_id', campaignId)
        .order('row_order')
    );
    
    if (error) throw new DatabaseError(`Failed to fetch campaign budgets: ${error.message}`);
  return ((result as any)?.data as CampaignBudget[]) || [];
  }

  async createCampaignBudget(budget: Omit<CampaignBudget, 'id' | 'created_at' | 'updated_at'>): Promise<CampaignBudget> {
    const [error, result] = await noderr(
      supabase
        .from('campaign_budgets')
        .insert(budget)
        .select(`
          *,
          category:budget_categories(*)
        `)
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to create campaign budget: ${error.message}`);
  return (result as any)?.data as CampaignBudget;
  }

  async updateCampaignBudget(id: string, updates: Partial<CampaignBudget>): Promise<CampaignBudget> {
    const [error, result] = await noderr(
      supabase
        .from('campaign_budgets')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          category:budget_categories(*)
        `)
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to update campaign budget: ${error.message}`);
  return (result as any)?.data as CampaignBudget;
  }

  async deleteCampaignBudget(id: string): Promise<void> {
    const [error] = await noderr(
      supabase
        .from('campaign_budgets')
        .delete()
        .eq('id', id)
    );
    
    if (error) throw new DatabaseError(`Failed to delete campaign budget: ${error.message}`);
  }

  // Budget Summary
  async getCampaignBudgetSummary(campaignId: string): Promise<BudgetSummary> {
    const budgets = await this.getCampaignBudgets(campaignId);
    
    const totalAllocated = budgets.reduce((sum, b) => sum + b.allocated_amount, 0);
    const totalSpent = budgets.reduce((sum, b) => sum + b.spent_amount, 0);
    const remainingBudget = totalAllocated - totalSpent;
    const spendPercentage = totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0;

    const categories: CategorySummary[] = budgets.map(budget => {
      const percentageUsed = budget.allocated_amount > 0 
        ? (budget.spent_amount / budget.allocated_amount) * 100 
        : 0;
      
      let status: CategorySummary['status'] = 'on_track';
      if (percentageUsed > 100) status = 'over_budget';
      else if (percentageUsed < 50) status = 'under_budget';

      return {
        category_id: budget.category_id,
        category_name: budget.category?.name || 'Unknown',
        category_color: budget.category?.color || '#3B82F6',
        allocated: budget.allocated_amount,
        spent: budget.spent_amount,
        remaining: budget.allocated_amount - budget.spent_amount,
        percentage_used: percentageUsed,
        performance_score: Math.random() * 100, // TODO: Calculate actual performance
        status
      };
    });

    return {
      campaign_id: campaignId,
      total_allocated: totalAllocated,
      total_spent: totalSpent,
      remaining_budget: remainingBudget,
      spend_percentage: spendPercentage,
      categories,
      performance_score: Math.random() * 100, // TODO: Calculate actual performance
      alerts_count: 0 // TODO: Get actual alerts count
    };
  }

  // Budget Snapshots
  async createBudgetSnapshot(campaignId: string, createdBy?: string): Promise<BudgetSnapshot> {
    const budgets = await this.getCampaignBudgets(campaignId);
    const summary = await this.getCampaignBudgetSummary(campaignId);

    const snapshotData = {
      budgets,
      summary,
      timestamp: new Date().toISOString()
    };

    const [error, result] = await noderr(
      supabase
        .from('budget_snapshots')
        .insert({
          campaign_id: campaignId,
          snapshot_data: snapshotData,
          total_allocated: summary.total_allocated,
          total_spent: summary.total_spent,
          created_by: createdBy
        })
        .select()
        .single()
    );
    
    if (error) throw new DatabaseError(`Failed to create budget snapshot: ${error.message}`);
  return (result as any)?.data as BudgetSnapshot;
  }

  async getBudgetSnapshots(campaignId: string): Promise<BudgetSnapshot[]> {
    const [error, result] = await noderr(
      supabase
        .from('budget_snapshots')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false })
    );
    
    if (error) throw new DatabaseError(`Failed to fetch budget snapshots: ${error.message}`);
  return ((result as any)?.data as BudgetSnapshot[]) || [];
  }

  async getRecentBudgetSnapshots(limit = 10): Promise<BudgetSnapshot[]> {
    const [error, result] = await noderr(
      supabase
        .from('budget_snapshots')
        .select(`
          *,
          campaign:campaigns(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit)
    );

    if (error) throw new DatabaseError(`Failed to fetch recent budget snapshots: ${error.message}`);
  return ((result as any)?.data as BudgetSnapshot[]) || [];
  }

  // AI Budget Oracle Methods (Placeholder for Phase 2)
  async generateBudgetSuggestions(_campaignId: string): Promise<BudgetSuggestion[]> {
    // Mock AI-powered budget suggestions
    // TODO: Replace with real AI integration
  const categories = await this.getBudgetCategories();
    
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const suggestions: BudgetSuggestion[] = [
      {
        category_id: categories.find(c => c.name.toLowerCase().includes('social'))?.id || 'social-ads',
        suggested_amount: 8500,
        confidence_score: 92,
        reasoning: 'Based on similar pop artists, social media advertising shows 3.2x higher engagement rates during pre-release campaigns. Your target demographic (18-34) is most active on TikTok and Instagram.',
        market_data: {
          genre_average: 7200,
          seasonal_factor: 1.15,
          competition_level: 'medium',
          trending_platforms: ['TikTok', 'Instagram', 'YouTube Shorts'],
          recommended_timing: 'Start 6 weeks before release'
        }
      },
      {
        category_id: categories.find(c => c.name.toLowerCase().includes('playlist'))?.id || 'playlist-pitching',
        suggested_amount: 5200,
        confidence_score: 88,
        reasoning: 'Playlist placement is crucial for pop genre. Our data shows artists with similar streaming history achieve 40% better playlist acceptance with professional pitching services.',
        market_data: {
          genre_average: 4800,
          seasonal_factor: 1.08,
          competition_level: 'high',
          trending_platforms: ['Spotify', 'Apple Music', 'Amazon Music'],
          recommended_timing: 'Submit 4-6 weeks before release'
        }
      },
      {
        category_id: categories.find(c => c.name.toLowerCase().includes('influencer'))?.id || 'influencer-marketing',
        suggested_amount: 3800,
        confidence_score: 76,
        reasoning: 'Micro-influencers (10K-100K followers) in your genre show better ROI than macro-influencers. Focus on music discovery and lifestyle content creators.',
        market_data: {
          genre_average: 4200,
          seasonal_factor: 0.95,
          competition_level: 'medium',
          trending_platforms: ['TikTok', 'Instagram', 'YouTube'],
          recommended_timing: 'Start 3 weeks before release'
        }
      }
    ];
    
    return suggestions;
  }

  async analyzeReallocationOpportunities(_campaignId: string): Promise<ReallocationSuggestion[]> {
    // Mock reallocation suggestions
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return [
      {
        from_category_id: 'radio-promotion',
        to_category_id: 'social-ads',
        amount: 2000,
        reason: 'Radio engagement is 23% below expected. Social ads are performing 45% above benchmark.',
        urgency: 'medium',
        potential_impact: 'Could increase overall campaign reach by 15-20%'
      },
      {
        from_category_id: 'print-advertising',
        to_category_id: 'playlist-pitching',
        amount: 1500,
        reason: 'Print advertising showing minimal impact for your demographic. Playlist opportunities available.',
        urgency: 'high',
        potential_impact: 'Potential for 300K+ additional streams'
      }
    ];
  }

  // Live Command Center Methods (Placeholder for Phase 3)
  async getRealtimeSpendUpdates(_campaignId: string) {
    // TODO: Implement WebSocket connection for real-time updates
    return null;
  }

  async executeBudgetReallocation(_fromBudgetId: string, _toBudgetId: string, _amount: number): Promise<void> {
    // TODO: Implement instant budget reallocation
    throw new Error('Budget reallocation not yet implemented');
  }

  // Meta Ads integration: fetch hourly spend and reconcile budget totals
  async syncMetaSpendForBudget(budgetId: string, externalCampaignId: string, sinceISO: string, untilISO: string) {
    const events = await metaAdsService.fetchHourlySpend(sinceISO, untilISO, [externalCampaignId]);
    await metaAdsService.ingestHourlySpend(events, undefined, budgetId);
    return await metaAdsService.reconcileBudgetSpent(budgetId);
  }
}

// Export singleton instance
export const budgetService = new BudgetService();
